import { useState, FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../api';
import { Shell } from './ForgotPassword';

const MIN_PASSWORD = 8; // matches the DTO policy on every server-side entry point

const ERROR_KEY: Record<string, string> = {
  reset_token_invalid: 'reset.invalid',
  weak_password: 'reset.tooShort',
  rate_limited: 'reset.rateLimited',
};

/**
 * Phase 6 step 2. Reads the token from ?token=, takes a new password.
 *
 * The token is never rendered or put anywhere it could be copied out of the
 * page — it is read from the query and posted, and nothing else.
 */
export function ResetPassword() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    // Checked here only to save a round trip and give the specific message;
    // the server enforces the same policy regardless of what this form allows.
    if (password.length < MIN_PASSWORD) return setError(t('reset.tooShort'));
    if (password !== confirm) return setError(t('reset.mismatch'));

    setBusy(true);
    setError(null);
    try {
      await api.resetPassword(token, password);
      setDone(true);
    } catch (err) {
      const key = err instanceof ApiError ? ERROR_KEY[err.message] : undefined;
      setError(t(key ?? 'reset.failed'));
    } finally {
      setBusy(false);
    }
  }

  // Someone reached /reset without a link — a bookmark, or a mail client that
  // ate the query string. Say so rather than showing a form that cannot work.
  if (!token) {
    return (
      <Shell>
        <div className="space-y-4">
          <h1 className="text-xl font-semibold text-slate-900">{t('reset.title')}</h1>
          <p role="alert" className="text-sm text-red-600 bg-red-50 rounded-md p-2">
            {t('reset.missingToken')}
          </p>
          <Link to="/forgot" className="block text-sm text-sky-600 hover:text-sky-700">
            {t('reset.requestNew')}
          </Link>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div className="space-y-4">
          <h1 className="text-xl font-semibold text-slate-900">{t('reset.doneTitle')}</h1>
          <p className="text-sm text-slate-600">{t('reset.done')}</p>
          <Link
            to="/login"
            className="block w-full text-center rounded-md bg-sky-600 px-4 py-2 text-white font-medium hover:bg-sky-700"
          >
            {t('reset.toLogin')}
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <form onSubmit={submit} className="space-y-4">
        <h1 className="text-xl font-semibold text-slate-900">{t('reset.title')}</h1>

        <div className="space-y-1">
          <label htmlFor="password" className="block text-sm font-medium text-slate-700">
            {t('reset.password')}
          </label>
          <input
            id="password"
            type="password"
            required
            minLength={MIN_PASSWORD}
            autoComplete="new-password"
            dir="ltr"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500"
          />
          <p className="text-xs text-slate-500">{t('reset.hint')}</p>
        </div>

        <div className="space-y-1">
          <label htmlFor="confirm" className="block text-sm font-medium text-slate-700">
            {t('reset.confirm')}
          </label>
          <input
            id="confirm"
            type="password"
            required
            autoComplete="new-password"
            dir="ltr"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-slate-900 focus:outline-none focus:ring-2 focus:ring-sky-500"
          />
        </div>

        {error && (
          <p role="alert" className="text-sm text-red-600 bg-red-50 rounded-md p-2">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={busy}
          className="w-full rounded-md bg-sky-600 px-4 py-2 text-white font-medium hover:bg-sky-700 disabled:opacity-50"
        >
          {busy ? t('reset.saving') : t('reset.submit')}
        </button>

        {/* An expired or spent link is the common failure here, and the only
            way forward is a fresh one. */}
        <Link to="/forgot" className="block text-sm text-sky-600 hover:text-sky-700">
          {t('reset.requestNew')}
        </Link>
      </form>
    </Shell>
  );
}
